/**
 * TrustAdminPage — Hustle Property Trust Account daily-driver.
 *
 * Tabs: Accounts · Matters · Receipts · Reconciliation · Bank feed ·
 *       Auditor access · Annual lodgement.
 *
 * All endpoints under /api/re-trust/* and the isolated HP_RE_TRUST_DB.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  Building2, FileText, Receipt, BarChart3, Banknote, Eye, Award,
  Plus, AlertTriangle, CheckCircle2, Loader2, ShieldCheck, Download,
  Hash, Calendar, ExternalLink, RefreshCw,
} from "lucide-react";
import "./TrustAdminPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

const TABS = [
  { id: "accounts",    label: "Accounts",    Icon: Building2  },
  { id: "matters",     label: "Matters",     Icon: FileText   },
  { id: "receipts",    label: "Receipts",    Icon: Receipt    },
  { id: "recon",       label: "Reconcile",   Icon: BarChart3  },
  { id: "bankfeed",    label: "Bank feed",   Icon: Banknote   },
  { id: "auditor",     label: "Auditor",     Icon: Eye        },
  { id: "lodgement",   label: "Lodgement",   Icon: Award      },
];

export default function TrustAdminPage() {
  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [activeAcc, setActiveAcc] = useState(null);
  const [chainOk, setChainOk] = useState(null);

  const refreshAccounts = useCallback(() => {
    api.get(`/api/re-trust/accounts`)
       .then(r => {
         setAccounts(r.data || []);
         if (!activeAcc && r.data?.[0]) setActiveAcc(r.data[0]);
       })
       .catch(() => {});
  }, [activeAcc]);

  useEffect(() => { refreshAccounts(); }, [refreshAccounts]);

  // Audit chain integrity ping
  useEffect(() => {
    api.get(`/api/re-trust/audit-trail/verify`)
       .then(r => setChainOk(r.data))
       .catch(() => setChainOk({ ok: false, error: true }));
  }, [accounts.length]);

  return (
    <div className="ta" data-testid="trust-admin-root">
      {/* Header */}
      <header className="ta-head">
        <div>
          <span className="ta-eyebrow">
            <ShieldCheck size={13}/> Hustle Property · Trust Accounting
          </span>
          <h1>Trust Account Console</h1>
          <p className="ta-sub">
            QLD <em>Agents Financial Administration Act 2014</em> compliant.
            Hash-chained audit trail · 2-business-day post tracking ·
            5-business-day month-end recon.
          </p>
        </div>
        {chainOk && (
          <div className={`ta-chain ${chainOk.ok ? "ok" : "bad"}`}
               data-testid="trust-chain-badge">
            <Hash size={14}/>
            {chainOk.ok
              ? `Audit chain verified · ${chainOk.entries || 0} entries`
              : "Audit chain compromised — review immediately"}
          </div>
        )}
      </header>

      {/* Account picker */}
      {accounts.length > 0 && (
        <div className="ta-accpick">
          <label>Working on:</label>
          <select value={activeAcc?.id || ""}
                  onChange={(e) =>
                    setActiveAcc(accounts.find(a => a.id === e.target.value))}
                  data-testid="trust-account-picker">
            {accounts.map(a => (
              <option key={a.id} value={a.id}>
                {a.licensee_name} · BSB {a.bsb} · {a.account_number}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tabs */}
      <nav className="ta-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            className={tab === id ? "is-active" : ""}
            onClick={() => setTab(id)}
            data-testid={`trust-tab-${id}`}>
            <Icon size={14}/> {label}
          </button>
        ))}
      </nav>

      <div className="ta-body">
        {tab === "accounts" && (
          <AccountsTab accounts={accounts} onRefresh={refreshAccounts}
                       active={activeAcc} onPick={setActiveAcc}/>
        )}
        {tab === "matters"   && <MattersTab/>}
        {tab === "receipts"  && <ReceiptsTab account={activeAcc}/>}
        {tab === "recon"     && <ReconTab    account={activeAcc}/>}
        {tab === "bankfeed"  && <BankFeedTab account={activeAcc}/>}
        {tab === "auditor"   && <AuditorTab  account={activeAcc}/>}
        {tab === "lodgement" && <LodgementTab account={activeAcc}/>}
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Accounts tab
// ──────────────────────────────────────────────────────────────────
function AccountsTab({ accounts, onRefresh }) {
  const [form, setForm] = useState({
    licensee_name: "Hustle Property Pty Ltd", licence_number: "PENDING",
    acn: "699315896", abn: "",
    bsb: "", account_number: "", bank_name: "", state: "QLD",
  });
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/api/re-trust/accounts`, form);
      setForm({ ...form, licence_number: "", bsb: "",
                account_number: "", bank_name: "" });
      onRefresh();
    } catch (_) { /* swallow */ }
    setBusy(false);
  };

  return (
    <div>
      <h2><Building2 size={16}/> Open trust accounts</h2>
      <table className="ta-table" data-testid="trust-accounts-table">
        <thead><tr><th>Licensee</th><th>Licence</th><th>Bank · BSB · Acc</th>
          <th>State</th><th>Receipt #</th></tr></thead>
        <tbody>
          {accounts.map(a => (
            <tr key={a.id}>
              <td><b>{a.licensee_name}</b></td>
              <td>{a.licence_number || "—"}</td>
              <td>{a.bank_name} · {a.bsb} · {a.account_number}</td>
              <td>{a.state}</td>
              <td>{a.next_receipt_no || 1}</td>
            </tr>
          ))}
          {accounts.length === 0 && (
            <tr><td colSpan={5} className="ta-empty">
              No accounts yet — open one below.
            </td></tr>
          )}
        </tbody>
      </table>

      <h2 style={{ marginTop: 28 }}><Plus size={16}/> Open new trust account</h2>
      <form onSubmit={create} className="ta-form" data-testid="trust-account-form">
        {[
          ["licensee_name", "Licensee name (Pty Ltd)"],
          ["licence_number", "Licence number (OFT)"],
          ["acn", "ACN"], ["abn", "ABN"],
          ["bank_name", "Bank"], ["bsb", "BSB"],
          ["account_number", "Account number"],
          ["state", "State"],
        ].map(([k, label]) => (
          <label key={k}>
            <span>{label}</span>
            <input
              value={form[k] || ""}
              onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))}
              required={["licensee_name","bsb","account_number","bank_name"].includes(k)}
              data-testid={`trust-acc-${k}`}
            />
          </label>
        ))}
        <button type="submit" disabled={busy} className="ta-btn"
                data-testid="trust-acc-submit">
          {busy ? <Loader2 size={14} className="ta-spin"/> : <Plus size={14}/>}
          {busy ? "Opening…" : "Open trust account"}
        </button>
      </form>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Matters tab
// ──────────────────────────────────────────────────────────────────
function MattersTab() {
  const [matters, setMatters] = useState([]);
  const [form, setForm] = useState({
    matter_type: "sale", property_address: "",
    sale_price_aud: "", contract_date: "", settlement_date: "",
  });
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    api.get(`/api/re-trust/matters?status=open`)
       .then(r => setMatters(r.data || [])).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form,
        sale_price_aud: form.sale_price_aud
          ? parseFloat(form.sale_price_aud) : undefined };
      await api.post(`/api/re-trust/matters`, payload);
      setForm({ matter_type: "sale", property_address: "",
                sale_price_aud: "", contract_date: "",
                settlement_date: "" });
      refresh();
    } catch (_) { /* swallow */ }
    setBusy(false);
  };

  return (
    <div>
      <h2><FileText size={16}/> Open matters</h2>
      <table className="ta-table" data-testid="trust-matters-table">
        <thead><tr><th>Type</th><th>Property</th><th>Sale price</th>
          <th>Trust balance</th><th>Settlement</th></tr></thead>
        <tbody>
          {matters.map(m => (
            <tr key={m.id} data-testid={`trust-matter-${m.id}`}>
              <td>{m.matter_type}</td>
              <td>{m.property_address}</td>
              <td>{m.sale_price_aud
                ? `$${m.sale_price_aud.toLocaleString()}` : "—"}</td>
              <td className="ta-bal">
                ${(m.balance_aud || 0).toLocaleString()}
              </td>
              <td>{(m.settlement_date || "")?.slice(0,10) || "—"}</td>
            </tr>
          ))}
          {matters.length === 0 && (
            <tr><td colSpan={5} className="ta-empty">
              No open matters yet.
            </td></tr>
          )}
        </tbody>
      </table>

      <h2 style={{ marginTop: 28 }}><Plus size={16}/> New matter</h2>
      <form onSubmit={create} className="ta-form" data-testid="trust-matter-form">
        <label><span>Type</span>
          <select value={form.matter_type}
                  onChange={(e) => setForm(f => ({ ...f, matter_type: e.target.value }))}>
            <option value="sale">Sale</option>
            <option value="auction">Auction</option>
            <option value="tenancy">Tenancy</option>
          </select>
        </label>
        <label><span>Property address</span>
          <input value={form.property_address}
                 onChange={(e) => setForm(f => ({ ...f, property_address: e.target.value }))}
                 required data-testid="trust-matter-address"/>
        </label>
        <label><span>Sale price (AUD)</span>
          <input value={form.sale_price_aud}
                 onChange={(e) => setForm(f => ({ ...f, sale_price_aud: e.target.value }))}
                 type="number" step="1000"/>
        </label>
        <label><span>Contract date</span>
          <input type="date" value={form.contract_date}
                 onChange={(e) => setForm(f => ({ ...f, contract_date: e.target.value }))}/>
        </label>
        <label><span>Settlement date</span>
          <input type="date" value={form.settlement_date}
                 onChange={(e) => setForm(f => ({ ...f, settlement_date: e.target.value }))}/>
        </label>
        <button type="submit" className="ta-btn" disabled={busy}
                data-testid="trust-matter-submit">
          {busy ? <Loader2 size={14} className="ta-spin"/> : <Plus size={14}/>}
          {busy ? "Saving…" : "Create matter"}
        </button>
      </form>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Receipts tab
// ──────────────────────────────────────────────────────────────────
function ReceiptsTab({ account }) {
  const [receipts, setReceipts] = useState([]);
  const refresh = useCallback(() => {
    if (!account) return;
    api.get(`/api/re-trust/accounts/${account.id}/receipts`)
       .then(r => setReceipts(r.data || []))
       .catch(() => {});
  }, [account]);
  useEffect(() => { refresh(); }, [refresh]);

  if (!account) return <Empty msg="Pick a trust account first."/>;
  return (
    <div>
      <h2>
        <Receipt size={16}/> Receipts journal ·{" "}
        <button className="ta-iconbtn" onClick={refresh}
                data-testid="trust-receipts-refresh">
          <RefreshCw size={12}/>
        </button>
      </h2>
      <table className="ta-table" data-testid="trust-receipts-table">
        <thead><tr><th>#</th><th>Date</th><th>Party</th><th>Purpose</th>
          <th>Method</th><th>Amount</th><th>Post</th></tr></thead>
        <tbody>
          {receipts.map(r => (
            <tr key={r.id}>
              <td><b>{r.receipt_no}</b></td>
              <td>{(r.received_at || "").slice(0, 10)}</td>
              <td>{r.party_id}</td>
              <td>{r.purpose}</td>
              <td>{r.payment_method}</td>
              <td className="ta-bal">${r.amount_aud.toLocaleString()}</td>
              <td>
                {r.late_post ? (
                  <span className="ta-late">
                    <AlertTriangle size={11}/> LATE ({r.days_to_post}d)
                  </span>
                ) : (
                  <span className="ta-ok">
                    <CheckCircle2 size={11}/> OK
                  </span>
                )}
              </td>
            </tr>
          ))}
          {receipts.length === 0 && (
            <tr><td colSpan={7} className="ta-empty">
              No receipts yet. Add one via{" "}
              <code>POST /api/re-trust/accounts/{account.id}/receipts</code>.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Reconciliation tab
// ──────────────────────────────────────────────────────────────────
function ReconTab({ account }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/api/re-trust/accounts/${account.id}/reconciliation`,
        { params: { year, month } });
      setRecon(r.data);
    } catch (_) { /* swallow */ }
    setLoading(false);
  }, [account, year, month]);

  useEffect(() => { run(); }, [run]);

  if (!account) return <Empty msg="Pick a trust account first."/>;
  const pdfUrl = `${API}/api/re-trust/accounts/${account.id}/reconciliation/pdf?year=${year}&month=${month}`;

  return (
    <div>
      <h2><BarChart3 size={16}/> Monthly reconciliation</h2>
      <div className="ta-recon-controls">
        <input type="number" value={year} min={2020} max={2099}
               onChange={(e) => setYear(parseInt(e.target.value))}
               data-testid="trust-recon-year"/>
        <select value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                data-testid="trust-recon-month">
          {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
            .map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
        </select>
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
           className="ta-btn ta-btn--secondary"
           data-testid="trust-recon-pdf">
          <Download size={13}/> Signed PDF
        </a>
      </div>

      {loading && <Empty msg="Building reconciliation…" loading/>}
      {recon && !loading && (
        <div className="ta-recon" data-testid="trust-recon-result">
          <div className="ta-recon-grid">
            <Stat label="Opening" v={recon.opening_balance_aud}/>
            <Stat label="+ Receipts" v={recon.receipts_total_aud}
                  sub={`${recon.receipt_count} entries`}/>
            <Stat label="− Payments" v={recon.payments_total_aud}
                  sub={`${recon.payment_count} entries`}/>
            <Stat label="Closing" v={recon.closing_balance_aud} big/>
          </div>
          <div className={`ta-banner ${recon.late_post_count
                                       ? 'warn' : 'ok'}`}>
            {recon.late_post_count
              ? <><AlertTriangle size={14}/> {recon.late_post_count} late post(s) — review for AFAA s.20</>
              : <><CheckCircle2 size={14}/> All entries within 2 business days. Compliant.</>}
          </div>
          <p className="ta-recon-due">
            <Calendar size={12}/> Hard copy due to OFT-aligned record by{" "}
            <b>{recon.report_due_by?.slice(0, 10)}</b> · audit chain hash:{" "}
            <code className="ta-chain-hash">
              {(recon.audit_chain_hash || "").slice(0, 32)}…
            </code>
          </p>
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Bank feed tab
// ──────────────────────────────────────────────────────────────────
function BankFeedTab({ account }) {
  const [unmatched, setUnmatched] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const refresh = useCallback(() => {
    if (!account) return;
    api.get(`/api/re-trust/bankfeed/${account.id}/unmatched`)
       .then(r => setUnmatched(r.data || []))
       .catch(() => {});
  }, [account]);
  useEffect(() => { refresh(); }, [refresh]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !account) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(
        `/api/re-trust/bankfeed/csv/${account.id}`, fd,
        { headers: { "Content-Type": "multipart/form-data" }});
      setResult(r.data); refresh();
    } catch (e2) {
      setResult({ error: e2?.response?.data?.detail || "Upload failed" });
    }
    setBusy(false);
  };

  if (!account) return <Empty msg="Pick a trust account first."/>;
  return (
    <div>
      <h2><Banknote size={16}/> Bank feed</h2>
      <div className="ta-feed-status">
        <p>
          <b>Live feed (preferred):</b> Connect via Basiq CDR Open Banking →
          POST events to{" "}
          <code>{API}/api/re-trust/bankfeed/basiq/webhook</code>.
          Most agencies use the Basiq <em>Affiliate</em> tier instead of full
          ADR accreditation.
        </p>
        <p>
          <b>Manual CSV (fallback):</b> Export transactions from CommBiz,
          Macquarie DEFT or NetBank and drop the file here.
        </p>
      </div>

      <form onSubmit={upload} className="ta-form" data-testid="trust-bankfeed-form">
        <label><span>Bank statement CSV</span>
          <input type="file" accept=".csv"
                 onChange={(e) => setFile(e.target.files?.[0])}
                 data-testid="trust-bankfeed-file"/>
        </label>
        <button type="submit" className="ta-btn" disabled={!file || busy}
                data-testid="trust-bankfeed-upload">
          {busy ? <Loader2 size={14} className="ta-spin"/> : <Banknote size={14}/>}
          {busy ? "Ingesting…" : "Upload + auto-match"}
        </button>
      </form>

      {result && (
        <div className={`ta-banner ${result.error ? 'warn' : 'ok'}`}>
          {result.error
            ? <><AlertTriangle size={14}/> {result.error}</>
            : <><CheckCircle2 size={14}/> Ingested {result.ingested}, matched {result.matched}, unmatched {result.unmatched}</>}
        </div>
      )}

      <h3 style={{ marginTop: 24 }}>Unmatched bank lines</h3>
      <table className="ta-table" data-testid="trust-bankfeed-unmatched">
        <thead><tr><th>Date</th><th>Description</th>
          <th>Direction</th><th>Amount</th><th>Source</th></tr></thead>
        <tbody>
          {unmatched.map(b => (
            <tr key={b.posted_date + b.amount_aud + b.description}>
              <td>{b.posted_date}</td>
              <td>{b.description}</td>
              <td className={b.direction === "credit" ? "ta-cr" : "ta-dr"}>
                {b.direction}
              </td>
              <td className="ta-bal">${b.amount_aud.toLocaleString()}</td>
              <td>{b.source}</td>
            </tr>
          ))}
          {unmatched.length === 0 && (
            <tr><td colSpan={5} className="ta-empty">
              All bank lines matched — nothing to investigate.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Auditor tab
// ──────────────────────────────────────────────────────────────────
function AuditorTab({ account }) {
  const today = new Date();
  const [form, setForm] = useState({
    auditor_name: "", auditor_email: "", auditor_firm: "",
    period_year: today.getFullYear(), expires_in_days: 30,
  });
  const [granted, setGranted] = useState(null);
  const [busy, setBusy] = useState(false);

  const grant = async (e) => {
    e.preventDefault();
    if (!account) return;
    setBusy(true);
    try {
      const r = await api.post(`/api/re-trust/auditor/grant`,
        { ...form, account_id: account.id });
      setGranted(r.data);
    } catch (_) { /* swallow */ }
    setBusy(false);
  };

  if (!account) return <Empty msg="Pick a trust account first."/>;
  return (
    <div>
      <h2><Eye size={16}/> Grant auditor read-only access</h2>
      <p className="ta-muted">
        Issue a time-boxed token for a registered company auditor
        (Corps Act 2001). They use the token to read receipts,
        payments, snapshots and verify the audit chain — but cannot
        modify any record.
      </p>
      <form onSubmit={grant} className="ta-form" data-testid="trust-auditor-form">
        <label><span>Auditor name</span>
          <input value={form.auditor_name} required
                 onChange={(e) => setForm(f => ({ ...f, auditor_name: e.target.value }))}
                 data-testid="trust-auditor-name"/>
        </label>
        <label><span>Auditor email</span>
          <input type="email" value={form.auditor_email} required
                 onChange={(e) => setForm(f => ({ ...f, auditor_email: e.target.value }))}
                 data-testid="trust-auditor-email"/>
        </label>
        <label><span>Firm</span>
          <input value={form.auditor_firm}
                 onChange={(e) => setForm(f => ({ ...f, auditor_firm: e.target.value }))}/>
        </label>
        <label><span>Financial year</span>
          <input type="number" value={form.period_year}
                 onChange={(e) => setForm(f => ({ ...f, period_year: parseInt(e.target.value) }))}/>
        </label>
        <label><span>Expires in (days)</span>
          <input type="number" value={form.expires_in_days}
                 onChange={(e) => setForm(f => ({ ...f, expires_in_days: parseInt(e.target.value) }))}/>
        </label>
        <button type="submit" className="ta-btn" disabled={busy}
                data-testid="trust-auditor-submit">
          {busy ? <Loader2 size={14} className="ta-spin"/> : <Eye size={14}/>}
          {busy ? "Granting…" : "Grant audit access"}
        </button>
      </form>

      {granted && (
        <div className="ta-grant" data-testid="trust-auditor-grant-result">
          <CheckCircle2 size={16}/>
          <div>
            <b>Audit token issued</b><br/>
            <small>Expires {(granted.expires_at || "").slice(0, 10)} ·
            Email sent to auditor</small>
            <div className="ta-token">{granted.token}</div>
            <a href={`/auditor?token=${granted.token}`} className="ta-btn"
               target="_blank" rel="noopener noreferrer"
               data-testid="trust-auditor-open">
              <ExternalLink size={12}/> Open auditor console
            </a>
          </div>
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Annual lodgement tab
// ──────────────────────────────────────────────────────────────────
function LodgementTab({ account }) {
  const today = new Date();
  // FY = year ending 30 Jun. If we're past Jul, the closing FY is this year.
  const fyDefault = today.getMonth() >= 6
    ? today.getFullYear() : today.getFullYear() - 1;
  const [fy, setFy] = useState(fyDefault);
  const [bundle, setBundle] = useState(null);
  const [busy, setBusy] = useState(false);

  const build = async () => {
    if (!account) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/api/re-trust/accounts/${account.id}/lodgement/${fy}`);
      setBundle(r.data);
    } catch (_) { /* swallow */ }
    setBusy(false);
  };

  if (!account) return <Empty msg="Pick a trust account first."/>;
  return (
    <div>
      <h2><Award size={16}/> Annual audit lodgement bundle</h2>
      <p className="ta-muted">
        Builds the 12-month summary, late-post register, FY totals and
        audit chain hash. Hand the PDF + this bundle to your auditor
        before they lodge with the QLD OFT.
      </p>
      <div className="ta-recon-controls">
        <label>FY ending 30 Jun
          <input type="number" value={fy}
                 onChange={(e) => setFy(parseInt(e.target.value))}
                 data-testid="trust-lodgement-fy"/>
        </label>
        <button onClick={build} className="ta-btn" disabled={busy}
                data-testid="trust-lodgement-build">
          {busy ? <Loader2 size={14} className="ta-spin"/> : <Award size={14}/>}
          {busy ? "Building…" : "Build lodgement bundle"}
        </button>
      </div>

      {bundle && (
        <div data-testid="trust-lodgement-result">
          <div className="ta-recon-grid">
            <Stat label="FY receipts"  v={bundle.fy_receipts_total}/>
            <Stat label="FY payments"  v={bundle.fy_payments_total}/>
            <Stat label="Late posts"   v={bundle.fy_late_posts} dollars={false}/>
          </div>
          <h3 style={{ marginTop: 16 }}>Month-by-month</h3>
          <table className="ta-table">
            <thead><tr><th>Month</th><th>Opening</th><th>Receipts</th>
              <th>Payments</th><th>Closing</th><th>Late</th></tr></thead>
            <tbody>
              {bundle.months.map(m => (
                <tr key={`${m.year}-${m.month}`}>
                  <td>{m.year}-{String(m.month).padStart(2, "0")}</td>
                  <td className="ta-bal">${m.opening.toLocaleString()}</td>
                  <td className="ta-bal">${m.receipts_total.toLocaleString()}</td>
                  <td className="ta-bal">${m.payments_total.toLocaleString()}</td>
                  <td className="ta-bal"><b>${m.closing.toLocaleString()}</b></td>
                  <td>{m.late_posts || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="ta-banner ok">
            <Hash size={13}/> Audit chain tip:{" "}
            <code className="ta-chain-hash">
              {(bundle.audit_chain_tip || "").slice(0, 44)}…
            </code>
          </div>
          <h3 style={{ marginTop: 16 }}>Next steps</h3>
          <ol className="ta-steps">
            {(bundle.next_steps || []).map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────
function Stat({ label, v, sub, big = false, dollars = true }) {
  return (
    <div className={`ta-stat ${big ? "big" : ""}`}>
      <span className="ta-stat__label">{label}</span>
      <span className="ta-stat__v">
        {dollars ? `$${(v || 0).toLocaleString(undefined,
          { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : v}
      </span>
      {sub && <small>{sub}</small>}
    </div>
  );
}

function Empty({ msg, loading = false }) {
  return (
    <div className="ta-empty-state">
      {loading
        ? <Loader2 className="ta-spin" size={22}/>
        : <FileText size={22}/>}
      <p>{msg}</p>
    </div>
  );
}
